import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { useLanguage } from '../i18n/LanguageProvider'
import { formatEuros } from '../lib/money'

// Phase 32 Plan 32-05 (Task 1 / B6 + E4) :
//   `promoUsed` prop removed — credits_balance.used_promo_code_id is dropped
//   by the same plan's migration. The promo affordance is now ALWAYS visible ;
//   is_new_customer_only enforcement lives server-side in the EF pre-check
//   (Decision #5 ii primary gate) + RPC soft-log defense-in-depth.
//
// Phase 47 Plan 47-03 (2026-05-26) — tarifs v2 horaire :
//   - Pack 5 retired from sale (D-05). The TS narrow `size: 10 | 30 | 50` is
//     the compile-time structural barrier — any `<PackCard size={5} />` JSX
//     now fails `tsc -b` (TS2322). PACK_DATA[5] is gone; historical
//     pack_purchases.pack_size=5 rows are rendered by the admin formatter
//     (AdminUtilisateurs) not by this component.
//   - Labels D-13 verbatim: 10h=220€, 30h=600€, 50h=800€.
//   - PACK_DATA exported so tests can assert the catalog shape directly
//     (defense-in-depth alongside the TS narrow).
//
// Phase 95 Plan 95-07 (2026-08-16) — catalogue de traduction :
//   PACK_DATA ne porte plus de LIBELLÉS mais des NOMBRES (centimes entiers +
//   pourcentage). Un libellé figé « 10 h — 220 € » ne peut pas devenir
//   « 10 h — €220 » : le symbole change de côté d'une langue à l'autre, ce
//   qu'aucune traduction de gabarit ne rattrape après coup. Les mêmes valeurs
//   D-13 sortent maintenant de `pack.title` / `pack.subtitle` interpolés avec
//   formatEuros — le rendu français est inchangé.
export type PackSize = 10 | 30 | 50

interface PackCardProps {
  size: PackSize
  origin: 'home' | 'packs' | 'dashboard'
}

export const PACK_DATA = {
  10: { priceCents: 22000, hourlyCents: 2200, discount: 12 },
  30: { priceCents: 60000, hourlyCents: 2000, discount: 20 },
  50: { priceCents: 80000, hourlyCents: 1600, discount: 36 },
} as const

export default function PackCard({ size, origin }: PackCardProps) {
  const { user, loading } = useAuth()
  const { lang, t } = useLanguage()
  const navigate = useNavigate()
  const [status] = useState<'idle' | 'loading' | 'error'>('idle')
  const [errorMsg] = useState<string | null>(null)
  const data = PACK_DATA[size]
  const footerId = `pack-${size}-footer`
  const title = t('pack.title', {
    hours: size,
    price: formatEuros(data.priceCents, lang),
  })
  const subtitle = t('pack.subtitle', {
    hourly: formatEuros(data.hourlyCents, lang),
    discount: data.discount,
  })

  function handleClick() {
    if (loading) return
    if (!user) {
      navigate(`/login?next=/packs/checkout/${size}`)
      return
    }
    // From Home/Dashboard/Packs, navigate to PackCheckout which calls the Edge Function.
    // (Keep PackCard simple : it never hits the network itself.)
    navigate(`/packs/checkout/${size}?origin=${origin}`)
  }

  return (
    <div className="bg-surface border border-border rounded-[8px] p-6 md:p-8 transition-all duration-200 flex flex-col">
      <div className="flex flex-col gap-4 flex-1">
        <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted block">
          {t('pack.eyebrow')}
        </span>
        <h3 className="font-display font-bold text-xl text-text">{title}</h3>
        <p className="text-text-muted text-base">{subtitle}</p>

        <div className="h-px bg-border mt-2" />

        <div id={footerId} className="text-text-muted text-sm leading-relaxed">
          {t('pack.terms')}
        </div>

        <button
          type="button"
          onClick={handleClick}
          disabled={status === 'loading' || loading}
          aria-describedby={footerId}
          className="mt-auto w-full bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 motion-safe:hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 cursor-pointer inline-flex items-center justify-center gap-2"
        >
          {status === 'loading' ? t('pack.redirecting') : t('pack.buy')}
        </button>

        {errorMsg && (
          <p role="alert" className="text-error text-sm mt-1">
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  )
}
